import { Box, CardContent, Link, Stack, Typography } from '@mui/material';
import { ONE_DAY_MILISECONDS } from 'app/appConstants';
import { MprcCard, MprcContainer, MprcJoinComponents, MprcRequestErrorReport, MprcRequestIndicator, MprcTypography } from 'components';
import { ClassroomMeetingAttendanceResponse } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import moment from 'moment';
import { useCoreApi } from 'providers/CoreApiProvider';
import { QRCodeCanvas } from 'qrcode.react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useParams } from 'react-router';
import { LmsPaths } from 'routes/LmsRouter';

////////////////////////////////////////////////////////////////////////////

export const MeetingQrCode: React.FC = () => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { classroomId, componentId } = useParams();

    const [loading, setLoading] = useState<boolean>(false);
    const [viewData, setViewData] = useState<ClassroomMeetingAttendanceResponse>();
    const [viewError, setViewError] = useState<any>();

    const eventRegistrationUrl = useMemo(() => window.location.protocol + "//" + window.location.host + "/" + LmsPaths.LmsEventRegistration + "?cid=" + componentId, [componentId]);

    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        setLoading(true);
        api.v1.classroomManagement.getMeetingAttendanceData({
            classroomId: classroomId!,
            componentId: componentId!,
            isQrCodeAttendance: true
        })
            .then(response => {
                setViewData(response.data);
            })
            .catch(e => setViewError(e))
            .finally(() => setLoading(false));
    }, []);

    const headerDates = useCallback((startDate: Date, endDate: Date) => {

        const diffDays = (endDate.getTime() - startDate.getTime()) / ONE_DAY_MILISECONDS;
        return diffDays > 1
            ? Formaters.performaFullDateRange(startDate, endDate, { dayOfWeek: true, longNames: false, replaceClosestDays: false, preposition: "always" })
                .split(' ').map((w, i) => (<>{w} {i == 3 ? <br /> : " "}</>))
            : (
                <>
                    {Formaters.performaDate(startDate, { dayOfWeek: true, longNames: true, replaceClosestDays: false, preposition: "none", excludeTime: true })}
                    <br />
                    {moment(startDate).format("HH:mm")} - {moment(endDate).format("HH:mm")}
                </>
            );
    }, []);

    const validatyRange = useCallback((startDate: Date, endDate: Date) => {

        const diffDays = (endDate.getTime() - startDate.getTime()) / ONE_DAY_MILISECONDS;
        return diffDays > 1
            ? t("Meeting.QrCode.ValidityText_other", {
                range: Formaters.performaFullDateRange(startDate, endDate, { dayOfWeek: false, longNames: false, replaceClosestDays: false })
            })
            : (
                t("Meeting.QrCode.ValidityText_one", {
                    range: [
                        t("shared:Literal.FromTime"),
                        moment(startDate).format("HH:mm"),
                        t("shared:Literal.ToTime"),
                        moment(endDate).format("HH:mm")
                    ].join(" ")
                })
            );
    }, []);


    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestIndicator visible={loading} />

            {
                viewError
                    ? <MprcRequestErrorReport error={viewError} />
                    : !loading && viewData &&
                    <>
                        <MprcContainer>
                            <Stack alignItems="end" sx={theme => ({ backgroundColor: theme.palette.notice.main, py: 1, px: 3 })} className="no-print">
                                <Link variant="body1" textTransform="uppercase" onClick={() => window.print()}>{t("Meeting.AttendanceList.PrintButtonText")}</Link>
                            </Stack>
                        </MprcContainer>

                        <Box margin="0 auto" width={572}>
                            <MprcCard
                                variant="outlined"
                                elevation={0}
                                borderRadius="md"
                                sx={theme => ({ borderColor: theme.palette.muted.light })}
                            >
                                <Box height="120px"
                                    bgcolor={theme => theme.palette.paper.main}
                                    sx={{
                                        printColorAdjust: "exact",
                                        WebkitPrintColorAdjust: "exact"
                                    }}
                                >
                                    <Stack spacing={1} justifyContent="center" px={2} height="100%">
                                        <MprcTypography noWrapRows={2} variant="h2" color="black">{viewData.meetingName}</MprcTypography>
                                        <Typography variant="h5" color="success.main" fontWeight="bold">
                                            {headerDates(viewData.startDate, viewData.endDate)}
                                        </Typography>
                                    </Stack>
                                </Box>
                                <CardContent sx={theme => ({
                                    height: 400,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderTop: 1,
                                    borderBottom: 1,
                                    borderColor: theme.palette.muted.light,
                                    borderWidth: 1
                                })}>
                                    <QRCodeCanvas
                                        size={300}
                                        value={eventRegistrationUrl}
                                    />
                                </CardContent>
                                <Box
                                    height="120px"
                                    bgcolor={theme => theme.palette.paper.main}
                                    sx={{
                                        printColorAdjust: "exact",
                                        WebkitPrintColorAdjust: "exact",
                                    }}
                                >
                                    <Stack spacing={1} alignItems="center" justifyContent="center" height="100%" width="100%">
                                        <Typography variant="overline" color="warning">
                                            {validatyRange(viewData.qrCodeStartDate, viewData.qrCodeEndDate)}
                                        </Typography>
                                        <Typography variant="overline" color="black" px={4}>
                                            {t("Meeting.QrCode.InstructionsText")}
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            <MprcJoinComponents
                                                components={[
                                                    viewData.trainingName,
                                                    viewData.multipleClasses ? viewData.className : null
                                                ]}
                                                separator="&bull;"
                                            />
                                        </Typography>
                                    </Stack>
                                </Box>
                            </MprcCard>
                        </Box>
                    </>
            }
        </>
    );
};
